import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { ShieldAlert, AlertTriangle, Clock, ArrowRight, Loader2 } from "lucide-react";

const KIND_LABELS = {
  blocked_state_attempt:   { label: "Blocked-state attempts",   tone: "red",    Icon: ShieldAlert },
  review_state_attempt:    { label: "Review-state attempts",    tone: "amber",  Icon: AlertTriangle },
  unclear_jurisdiction:    { label: "Unclear remote/multi-state jurisdiction", tone: "amber", Icon: AlertTriangle },
  special_regulated_review:{ label: "Special regulated review", tone: "amber",  Icon: AlertTriangle },
  state_activated:         { label: "States activated",         tone: "emerald",Icon: ShieldAlert },
  notify_me:               { label: "Notify-me requests",       tone: "sky",    Icon: ShieldAlert },
  license_expiring:        { label: "License / registration expiring", tone: "red", Icon: Clock },
  review_due:              { label: "Compliance review due",    tone: "amber",  Icon: Clock },
};

export default function ComplianceAttentionWidget() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/compliance/attention-summary")
      .then(r => setD(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err) return null; // non-admin users get 403 — just hide
  if (!d) return (
    <div data-testid="compliance-widget-loading" className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading compliance signals…</div>
    </div>
  );

  const buckets = [
    ...Object.keys(d.counts).filter(k => k !== "license_expiring" && k !== "review_due").map(k => ({
      key: k, count: d.counts[k], items: (d.recent?.[k] || []),
      meta: KIND_LABELS[k] || { label: k.replaceAll("_"," "), tone: "slate", Icon: AlertTriangle },
    })),
    { key: "license_expiring", count: d.counts.license_expiring, items: d.license_expiring, meta: KIND_LABELS.license_expiring, isRegistry: true },
    { key: "review_due", count: d.counts.review_due, items: d.review_due, meta: KIND_LABELS.review_due, isRegistry: true },
  ];
  const total = d.total_open || 0;

  return (
    <div data-testid="compliance-attention-widget" className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600">Compliance · Attention Center</div>
          <div className="font-display text-lg font-bold text-slate-900">State Compliance Signals <span className="text-slate-400 font-mono text-sm ml-1">({total})</span></div>
          <div className="text-[11px] text-slate-500">Last {d.window_days} days · sourced from the existing Compliance Attention Center — no duplicate storage.</div>
        </div>
        <Link to="/admin/compliance" data-testid="widget-open-compliance"
              className="text-xs font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1">Open State Compliance <ArrowRight size={12}/></Link>
      </div>

      {total === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-4">No compliance exceptions in the last {d.window_days} days. ✔</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {buckets.filter(b => b.count > 0).map(b => {
            const Icon = b.meta.Icon;
            const tone = b.meta.tone;
            return (
              <div key={b.key} data-testid={`bucket-${b.key}`} className={`border rounded-lg p-3 bg-${tone}-50/40 border-${tone}-200`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className={`inline-flex items-center gap-1.5 text-xs font-semibold text-${tone}-800`}>
                    <Icon size={13}/> {b.meta.label}
                  </div>
                  <span className={`text-[10px] font-mono font-bold text-${tone}-800`}>{b.count}</span>
                </div>
                <div className="space-y-1">
                  {b.items.slice(0, 3).map((it, i) => (
                    <div key={it.id || i} className="text-[11px] text-slate-700 truncate">
                      {b.isRegistry ? (
                        <>· <span className="font-semibold">{it.state}</span> ({it.state_code}) — {it.expiration_date?.slice(0,10) || it.next_review_date?.slice(0,10)}</>
                      ) : (
                        <>· {it.payload?.state || it.payload?.state_code || "—"} · <span className="text-slate-500">{it.payload?.context || it.payload?.service_category || it.payload?.reason || ""}</span></>
                      )}
                    </div>
                  ))}
                  {b.items.length === 0 && <div className="text-[11px] text-slate-400 italic">No details available.</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
